import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import { IMqttMessage } from "ngx-mqtt";
import { Observable, BehaviorSubject, Subscription } from "rxjs";
import { debounceTime, filter, map, withLatestFrom } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AccessHistory } from "src/model/class/general classes/access-history";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { AccessResult } from "src/components/shared-components/enum/enumAccessControl";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";

@Injectable({
  providedIn: "root",
})
export class AccessHistoryService implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "access-history.service";
  private _accessHistoryStore: GenericApiData = null;
  private _accessHistorySubject: BehaviorSubject<Array<AccessHistory>> =
    new BehaviorSubject<Array<AccessHistory>>(null);

  private _isErrorNotSupported: boolean = false;
  private _isErrorInGetAccessHistoryListAPIRequest: boolean = false;

  private mutex = new Mutex();

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;
  _eventSubscriptions: Array<Subscription> = null;

  public needToInitializa: boolean = true;
  public readonly accessHistoryList = this._accessHistorySubject.asObservable();

  get IsErrorInGetAccessHistoryListAPIRequest(): boolean {
    return this._isErrorInGetAccessHistoryListAPIRequest;
  }

  get IsErrorNotSupported(): boolean {
    return this._isErrorNotSupported;
  }

  /** Subscribe to events and MQTTAddresses observables */
  constructor(
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _store: Store
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  /** Unsubscribe from observables */
  ngOnDestroy() {
    this._accessHistorySubject.next(null);
    this._accessHistorySubject.complete();

    this.unsubscribeEvents();
    this.unsubscribeToMQTTAddresses();
  }

  /** Subscribe to EventService observables */
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        await this.unsubscribeToMQTTAddresses();

        this._accessHistoryStore = null;
        this._accessHistorySubject.next(null);
        this.needToInitializa = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  /** Unsubscribe from all EventService observables */
  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) sub.unsubscribe();
  }

  //** NOT IMPLEMENTED YET */
  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_ACCESS_HISTORY_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._accessHistoryStore !== null),
        map((mqttMessage: IMqttMessage) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "subscribeMQTTAddresses()",
            `JSON message: ${mqttMessage}`
          );

          let message = JSON.parse(
            mqttMessage.payload.toString()
          ).Message.Value.split("|");

          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "subscribeMQTTAddresses()",
            `${message}`
          );

          message.push(
            JSON.parse(mqttMessage.payload.toString()).Message.ClientID
          );
          return message;
        }),
        map(async (message: Array<string>) => {
          if (message.length === 3) {
            const release = await this.mutex.acquire();

            try {
              const operationKind = parseInt(message[0]);
              const idAccessHistory = parseInt(message[1]);
              const clientID = message[2];

              if (operationKind === GenericOperationKind.INSERT) {
              } else if (operationKind === GenericOperationKind.UPDATE) {
              } else if (operationKind === GenericOperationKind.DELETE) {
              }
              return `${idAccessHistory}|${clientID}`;
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnAccessHistoryDataChange - Incorrect format message"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDAccessHistory) => {
          promiseIDAccessHistory.then();
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "subscribeMQTTAddresses()",
            "OnAccessHistoryChanged - incorrect message format"
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
  }

  /** NOT IMPLEMENTED YET */
  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY Present" + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  /** NOT IMPLEMENTED YET */
  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  /** Get Access History via API Call, using filter as queryString */
  public GetAccessHistory(filter: string): Promise<AccessHistory[]> {
    return this._http
      .get<Array<AccessHistory>>(
        environment.httpServerRoot + AppSettings.API_URL_ACCESS_HISTORY + filter
      )
      .toPromise();
  }

  /**
   * Get history acces by id card
   *
   * @param idCar ID card to get access
   * @returns
   */
  public async getAccessHistoryByIDCard(
    idCard: number
  ): Promise<AccessHistory[]> {
    return await this.GetAccessHistory("?cardId=" + idCard);
  }

  /**
   * Get icon by access result
   */
  public getIconByAccessResult(accessResult: AccessResult): string {
    let nameIcon: string = "";

    switch (accessResult) {
      case AccessResult.ACCESS_ALLOWED:
        nameIcon = "checkmark-circle";
        break;
      case AccessResult.ACCESS_DENIED:
        nameIcon = "close-circle";
        break;
      case AccessResult.ACCESS_DENIED_EXPIRED_CARD:
      case AccessResult.ACCESS_DENIED_LOCK_CARD:
      case AccessResult.ACCESS_DENIED_NOT_VALID_CARD:
        nameIcon = "card";
        break;
      case AccessResult.ACCESS_DENIED_WRONG_CALENDAR:
        nameIcon = "calendar";
        break;
      case AccessResult.ACCESS_DENIED_WRONG_TIMESLOT:
        nameIcon = "calendar";
        break;
      case AccessResult.ACCESS_DENIED_WRONG_PLANT_CODE:
        nameIcon = "close-circle";
        break;
      default:
        nameIcon = "help";
        break;
    }

    return nameIcon;
  }
}
